import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    TextField,
    Chip,
} from "@mui/material";
import { authHeader } from "../auth";

// Vue d'ensemble des pénalités télémark — croise, pour CHAQUE dossard (dans
// l'ordre de la startlist, toutes parités confondues), les saisies des 2
// juges style et du juge jump sur une seule ligne. Sert à la fois à
// CONTRÔLER visuellement ce qui a déjà été saisi (case vide = pas encore
// saisi) et à saisir/corriger directement n'importe quelle case.
//
// URL : /saisiepenalite/:eventKey/overview
//
// Contrairement à SaisiePenalite.jsx (un seul poste à la fois), cette page
// n'est pas conçue pour la saisie rapide "sur le vif" d'un juge assis à son
// poste, mais pour une relecture/contrôle global — typiquement le chef
// chronométreur qui vérifie que tout est cohérent avant de valider.

const POLL_MS = 5000;

const CELLS = [
    { category: "style", slot: 1, label: "Style J1" },
    { category: "style", slot: 2, label: "Style J2" },
    { category: "jump", slot: 1, label: "Jump" },
];
const STYLE_CELLS = CELLS.filter((c) => c.category === "style");
const JUMP_CELLS = CELLS.filter((c) => c.category === "jump");

const STYLE_MIN = 0;
const STYLE_MAX = 15;
const STYLE_WARNING_THRESHOLD = 7; // au-delà : avertissement visuel
const JUMP_VALUES = [0, 1, 2, 3];

function athleteName(a) {
    return `${a.lastname || ""} ${a.firstname || ""}`.trim();
}

function cellKey(bib, category, slot) {
    return `${bib}-${category}-${slot}`;
}

export default function SaisiePenaliteOverview() {
    const { eventKey } = useParams();

    const [startlist, setStartlist] = useState([]);
    const [entries, setEntries] = useState([]);
    const [error, setError] = useState("");
    const [info, setInfo] = useState("");
    const [values, setValues] = useState({}); // cellKey -> string affiché/édité
    const [savingKey, setSavingKey] = useState(null);

    // Cases modifiées localement mais pas encore confirmées par le serveur —
    // on ne les écrase pas au prochain rafraîchissement automatique, sinon
    // la saisie en cours disparaîtrait sous les yeux de l'utilisateur.
    const dirtyKeys = useRef(new Set());

    const apiFetch = useCallback(
        (url, options = {}) =>
            fetch(url, {
                ...options,
                headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) },
            }),
        []
    );

    const loadStartlist = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/timing/startlist/${encodeURIComponent(eventKey)}`);
            if (res.ok) setStartlist(await res.json());
        } catch (err) {
            setError(err.message);
        }
    }, [apiFetch, eventKey]);

    const loadEntries = useCallback(async () => {
        try {
            const res = await apiFetch(`/api/timing/penalty-entries/${encodeURIComponent(eventKey)}`);
            if (res.ok) setEntries(await res.json());
        } catch (err) {
            setError(err.message);
        }
    }, [apiFetch, eventKey]);

    useEffect(() => {
        loadStartlist();
        loadEntries();
        const t = setInterval(loadEntries, POLL_MS);
        return () => clearInterval(t);
    }, [loadStartlist, loadEntries]);

    // Synchronise l'affichage depuis les entrées serveur, sauf les cases
    // marquées "dirty" (édition locale en cours, pas encore sauvegardée).
    useEffect(() => {
        setValues((prev) => {
            const next = { ...prev };
            entries.forEach((e) => {
                const key = cellKey(e.bib, e.category, Number(e.slot));
                if (!dirtyKeys.current.has(key)) {
                    next[key] = String(e.value ?? "");
                }
            });
            return next;
        });
    }, [entries]);

    const sortedStartlist = useMemo(
        () => [...startlist].sort((a, b) => Number(a.bib) - Number(b.bib)),
        [startlist]
    );

    // Style : uniquement des chiffres, pas de virgule/point ni de signe —
    // on filtre à la frappe plutôt que de valider après coup.
    const handleStyleChange = (key, val) => {
        const digitsOnly = val.replace(/[^0-9]/g, "");
        dirtyKeys.current.add(key);
        setValues((prev) => ({ ...prev, [key]: digitsOnly }));
    };

    // Jump : champ texte (clavier numérique) — un seul chiffre, la plage
    // 0-3 est vérifiée à l'enregistrement (voir saveCell). Le tap rapide
    // (boutons 0/1/2/3, voir renderCell) passe aussi par cette fonction.
    const handleJumpChange = (key, val) => {
        const digitsOnly = val.replace(/[^0-9]/g, "").slice(0, 1);
        dirtyKeys.current.add(key);
        setValues((prev) => ({ ...prev, [key]: digitsOnly }));
    };

    const saveCell = async (bib, category, slot) => {
        const key = cellKey(bib, category, slot);
        const raw = values[key] ?? "";
        if (raw.trim() === "") {
            dirtyKeys.current.delete(key);
            return;
        }

        const value = parseInt(raw, 10);
        if (!Number.isInteger(value)) {
            setError("La pénalité doit être un nombre entier.");
            return;
        }
        if (category === "style" && (value < STYLE_MIN || value > STYLE_MAX)) {
            setError(`Le style doit être un entier entre ${STYLE_MIN} et ${STYLE_MAX}.`);
            return;
        }
        if (category === "jump" && !JUMP_VALUES.includes(value)) {
            setError(`Le jump doit être ${JUMP_VALUES.join(", ")}.`);
            return;
        }

        setSavingKey(key);
        setError("");
        setInfo("");
        try {
            const res = await apiFetch(
                `/api/timing/penalty-entries/${encodeURIComponent(eventKey)}/${encodeURIComponent(bib)}/${category}/${slot}`,
                { method: "POST", body: JSON.stringify({ value }) }
            );
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }
            dirtyKeys.current.delete(key);
            setEntries((prev) => [
                ...prev.filter((e) => !(String(e.bib) === String(bib) && e.category === category && Number(e.slot) === slot)),
                { bib, category, slot, value, updatedAt: new Date().toISOString() },
            ]);
            setInfo(`Dossard ${bib} — ${category === "style" ? `Style J${slot}` : "Jump"} enregistré ✓`);
        } catch (err) {
            setError(err.message);
        } finally {
            setSavingKey(null);
        }
    };

    const rows = sortedStartlist.map((a) => {
        const bib = String(a.bib);
        const cells = CELLS.map(({ category, slot }) => {
            const key = cellKey(bib, category, slot);
            return { key, category, slot, value: values[key] ?? "" };
        });
        const filledCount = cells.filter((c) => c.value.trim() !== "").length;
        const styleSubtotal = cells
            .filter((c) => c.category === "style")
            .reduce((sum, c) => sum + (parseInt(c.value, 10) || 0), 0);
        return { athlete: a, bib, cells, filledCount, styleSubtotal };
    });

    // Rendu d'une cellule éditable (Select fermé pour jump, champ entier
    // avec avertissement pour style) — appelé deux fois par ligne (cellules
    // style, puis cellules jump), avec le sous-total Style intercalé entre
    // les deux dans le JSX du tableau.
    const renderCell = (c, row) => {
        const isJump = c.category === "jump";
        const numValue = c.value.trim() === "" ? null : parseInt(c.value, 10);
        const isWarning = !isJump && numValue != null && numValue > STYLE_WARNING_THRESHOLD;
        const filledBg = c.value.trim() !== "" ? (isWarning ? "#fff3e0" : "#e8f5e9") : "transparent";

        return (
            <TableCell key={c.key} align="center">
                {isJump ? (
                    <Box sx={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                        {/* Clavier numérique : taper directement le chiffre */}
                        <TextField
                            size="small"
                            type="text"
                            inputMode="numeric"
                            value={c.value}
                            placeholder="—"
                            onChange={(e) => handleJumpChange(c.key, e.target.value)}
                            onBlur={() => saveCell(row.bib, c.category, c.slot)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") e.target.blur();
                            }}
                            disabled={savingKey === c.key}
                            sx={{
                                width: 56,
                                "& input": { textAlign: "center", py: 0.5 },
                                "& .MuiOutlinedInput-root": { backgroundColor: filledBg },
                            }}
                        />
                        {/* Tap rapide : mêmes 4 valeurs possibles, en un geste */}
                        <Box sx={{ display: "flex", gap: 0.25 }}>
                            {JUMP_VALUES.map((v) => (
                                <Box
                                    key={v}
                                    component="button"
                                    type="button"
                                    onClick={() => {
                                        handleJumpChange(c.key, String(v));
                                        setTimeout(() => saveCell(row.bib, c.category, c.slot), 0);
                                    }}
                                    disabled={savingKey === c.key}
                                    sx={{
                                        width: 18,
                                        height: 18,
                                        fontSize: "0.65rem",
                                        lineHeight: 1,
                                        cursor: "pointer",
                                        border: "1px solid #ccc",
                                        borderRadius: "3px",
                                        backgroundColor: c.value === String(v) ? "primary.main" : "#fff",
                                        color: c.value === String(v) ? "#fff" : "inherit",
                                        p: 0,
                                    }}
                                >
                                    {v}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ) : (
                    <TextField
                        size="small"
                        type="text"
                        inputMode="numeric"
                        value={c.value}
                        placeholder="—"
                        onChange={(e) => handleStyleChange(c.key, e.target.value)}
                        onBlur={() => saveCell(row.bib, c.category, c.slot)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") e.target.blur();
                        }}
                        disabled={savingKey === c.key}
                        sx={{
                            width: 72,
                            "& input": {
                                textAlign: "center",
                                py: 0.5,
                                fontWeight: isWarning ? 700 : 400,
                                color: isWarning ? "#e65100" : "inherit",
                            },
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: filledBg,
                            },
                        }}
                    />
                )}
            </TableCell>
        );
    };

    return (
        <Box p={3}>
            <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.5 }}>
                Pénalités — vue d'ensemble — {eventKey}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Contrôle et saisie directe : une case vide = pas encore saisie. Cliquez dans une case pour
                corriger, puis Entrée ou clic ailleurs pour enregistrer. Style : entier de {STYLE_MIN} à{" "}
                {STYLE_MAX} (surligné au-delà de {STYLE_WARNING_THRESHOLD}). Jump : {JUMP_VALUES.join(", ")}.
            </Typography>

            {error && (
                <Typography color="error" mb={2}>
                    {error}
                </Typography>
            )}
            {info && !error && (
                <Typography color="success.main" mb={2}>
                    {info}
                </Typography>
            )}

            <Paper variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold" }}>Dossard</TableCell>
                            <TableCell sx={{ fontWeight: "bold" }}>Nom</TableCell>
                            {STYLE_CELLS.map((c) => (
                                <TableCell key={c.label} align="center" sx={{ fontWeight: "bold" }}>
                                    {c.label}
                                </TableCell>
                            ))}
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                Sous-total Style
                            </TableCell>
                            {JUMP_CELLS.map((c) => (
                                <TableCell key={c.label} align="center" sx={{ fontWeight: "bold" }}>
                                    {c.label}
                                </TableCell>
                            ))}
                            <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                État
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, i) => (
                            <TableRow
                                key={row.bib}
                                sx={{ backgroundColor: i % 2 === 0 ? "#fafafa" : "transparent" }}
                            >
                                <TableCell sx={{ fontWeight: "bold" }}>{row.bib}</TableCell>
                                <TableCell>{athleteName(row.athlete) || "—"}</TableCell>

                                {row.cells.filter((c) => c.category === "style").map((c) => renderCell(c, row))}

                                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                    {row.styleSubtotal}
                                </TableCell>

                                {row.cells.filter((c) => c.category === "jump").map((c) => renderCell(c, row))}

                                <TableCell align="center">
                                    <Chip
                                        size="small"
                                        label={`${row.filledCount}/${CELLS.length}`}
                                        color={
                                            row.filledCount === CELLS.length
                                                ? "success"
                                                : row.filledCount === 0
                                                    ? "default"
                                                    : "warning"
                                        }
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                        {rows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    Aucun dossard dans la startlist.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
}